import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PermissionFlags } from '../PermissionFlags/PermissionFlags';
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { ButtonV2 } from '../ButtonV2/ButtonV2';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../Dialog/Dialog';
import { useTranslation } from 'react-i18next';

export interface SubmenuItem {
  text: string;
  href?: string;
  onClick?: () => void;
  isDisabled?: boolean;
  flags?: string[];
  icon?: React.ReactNode;
  name?: string;
}

export interface MenuItem {
  text: string;
  href?: string;
  name?: string;
  isDisabled?: boolean;
  flags?: string[];
  submenuItems?: SubmenuItem[];
  icon?: React.ReactNode;
}

interface Props {
  menuItems: MenuItem[];
  bottomMenuItems: MenuItem[];
}

const SIDEBAR_SAVING = 'sidebar-collapsed';

const Sidebar: React.FC<Props> = ({ menuItems, bottomMenuItems }) => {
  const [openSubmenus, setOpenSubmenus] = useState<{ [key: number]: boolean }>({});
  const [openSubmenusBottom, setOpenSubmenusBottom] = useState<{ [key: number]: boolean }>({});
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    const savedState = localStorage.getItem(SIDEBAR_SAVING);
    return savedState ? JSON.parse(savedState) : false;
  });
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();

  const isActive = (href: string) => {
    return location.pathname === href;
  }

  const toggleSubmenu = (index: number) => {
    setOpenSubmenus(prevState => ({
      ...prevState,
      [index]: !prevState[index]
    }));
  };

  const toggleSubmenuBottom = (index: number) => {
    setOpenSubmenusBottom(prevState => ({
      ...prevState,
      [index]: !prevState[index]
    }));
  };

  const toggleSidebar = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    localStorage.setItem(SIDEBAR_SAVING, JSON.stringify(newCollapsedState));
  };

  return (
    <aside className={`app-sidebar hidden md:flex md:flex-col md:relative ${isCollapsed ? 'w-16' : 'w-64'} p-4 px-7 h-screen z-5 shadow-sm overflow-y-auto transition-width duration-300`}>
      <div className="flex-1 flex flex-col justify-between">
        <nav className={`${isCollapsed ? 'w-15' : 'w-52'} overflow-y-auto pl-5 md:fixed md:left-0 md:h-[calc(100vh-300px)]`}>
          <ul>
            {menuItems.map((item, index) => (
              <PermissionFlags requiredFlags={item.flags} key={index}>
                <li key={index} className="mb-2">
                  {item.submenuItems ? (
                    <>
                      <button
                        onClick={() => toggleSubmenu(index)}
                        className={`w-full flex items-center p-2 rounded focus:outline-none ${
                          item.isDisabled 
                            ? 
                            'cursor-not-allowed text-gray-400' : 
                            'hover-underline-animation cursor-pointer'
                        }`}
                      >
                        {item.icon}
                        {!isCollapsed && 
                        <>
                          <span className="ml-2 text-base font-sans font-semibold tracking-tight text-slate-700">{item.text}</span>
                        </>
                        }
                      </button>
                      {openSubmenus[index] && (
                        <ul className={`${isCollapsed ? '' : 'ml-6'} mt-1`}>
                          {item.submenuItems.map((subItem, subIndex) => (
                            <PermissionFlags requiredFlags={subItem.flags} key={subIndex}>
                              <li key={subIndex} className="mb-1">
                                {subItem.isDisabled ? (
                                  <span
                                    className="block p-2 rounded text-base text-gray-400 cursor-not-allowed"
                                  >
                                    {subItem.icon}
                                    {!isCollapsed && <span className="ml-2 text-base font-sans font-medium tracking-tight">{subItem.text}</span>}
                                  </span>
                                ) : (
                                  <Link
                                    to={subItem.href || '#'}
                                    className={`block p-2 rounded text-base font-sans font-medium tracking-tight ${
                                      subItem.isDisabled
                                        ? 'text-slate-400 cursor-not-allowed'
                                        : isActive(subItem.href!)
                                          ? 'text-slate-900 bg-slate-100'
                                          : 'text-slate-700 hover:bg-slate-100'
                                    }`}
                                    onClick={subItem.onClick}
                                  >
                                    {subItem.icon}
                                    {!isCollapsed && <span className="ml-2 text-base font-sans font-medium tracking-tight">{subItem.text}</span>}
                                  </Link>
                                )}
                              </li>
                            </PermissionFlags>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    item.isDisabled ? (
                      <span
                        className="block p-2 rounded text-base font-sans font-medium text-slate-400 cursor-not-allowed"
                      >
                        {item.icon}
                        {!isCollapsed && <span className="ml-2 text-base font-sans font-medium tracking-tight">{item.text}</span>}
                      </span>
                    ) : (
                      <Link
                        to={item.href || '#'}
                        className={`block p-2 rounded text-base font-sans font-semibold tracking-tight ${
                          item.isDisabled
                            ? 'text-slate-400 cursor-not-allowed'
                            : isActive(item.href!)
                              ? 'text-slate-900 bg-slate-100'
                              : 'text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        {item.icon}
                        {!isCollapsed && <span className="ml-2 text-base font-sans font-semibold tracking-tight">{item.text}</span>}
                      </Link>
                    )
                  )}
                </li>
              </PermissionFlags>
            ))}
          </ul>
        </nav>
        <div className="flex-grow"></div>
        <div className={`pl-5 ${isCollapsed ? 'w-12' : 'w-52'} md:fixed md:bottom-0 md:left-0`}>
          {/* <button 
            onClick={() => setIsModalOpen(true)}
            type="button" 
            className={`
              mb-5 w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-2 rounded
              transition ${isCollapsed ? 'text-xs' : ''}
              relative overflow-hidden
              animate-pulse duration-[10000]
            `}
            data-dd-action-name="Get New Website Offer"
          >
            {isCollapsed ? <Globe className="w-4 h-4 ml-0" /> : t("website.upgrade.menu")}
          </button> */}
          <nav>
            <ul>
              {bottomMenuItems.map((item, index) => (
                <PermissionFlags requiredFlags={item.flags} key={index}>
                  <li key={index} className="mb-2">
                    {item.submenuItems ? (
                      <>
                        <button
                          onClick={() => toggleSubmenuBottom(index)}
                          className={`w-full flex items-center p-2 rounded focus:outline-none ${
                            item.isDisabled ? 'cursor-not-allowed text-gray-400' : 'hover-underline-animation cursor-pointer'
                          }`}
                        >
                          {item.icon}
                          {!isCollapsed &&
                            <>
                              <span className="ml-2 text-base font-sans font-semibold tracking-tight">{item.text}</span>
                            </>
                          }
                        </button>
                        {openSubmenusBottom[index] && (
                          <ul className={`${isCollapsed ? '' : 'ml-6'} mt-1`}>
                            {item.submenuItems.map((subItem, subIndex) => (
                              <PermissionFlags requiredFlags={subItem.flags} key={subIndex}>
                                <li key={subIndex} className="mb-1">
                                  {subItem.isDisabled ? (
                                    <span
                                      className="block p-2 rounded text-gray-400 cursor-not-allowed"
                                    >
                                      {subItem.icon}
                                      {!isCollapsed && <span className="ml-2 text-base font-sans font-medium tracking-tight">{subItem.text}</span>}
                                    </span>
                                  ) : (
                                    <Link
                                      to={subItem.href || '#'}
                                      className={`block p-2 rounded text-base font-sans font-medium tracking-tight ${
                                        subItem.isDisabled
                                          ? 'text-slate-400 cursor-not-allowed'
                                          : isActive(subItem.href!)
                                            ? 'text-slate-900 bg-slate-100'
                                            : 'text-slate-700 hover:bg-slate-100'
                                      }`}
                                      onClick={subItem.onClick}
                                    >
                                      {subItem.icon}
                                      {!isCollapsed && <span className="ml-2 text-base font-sans font-medium tracking-tight">{subItem.text}</span>}
                                    </Link>
                                  )}
                                </li>
                              </PermissionFlags>
                            ))}
                          </ul>
                        )}
                      </>
                    ) : (
                      item.isDisabled ? (
                        <span
                          className="block p-2 rounded text-base font-sans font-medium text-slate-400 cursor-not-allowed"
                        >
                          {item.icon}
                          {!isCollapsed && <span className="ml-2 text-base font-sans font-medium tracking-tight">{item.text}</span>}
                        </span>
                      ) : (
                        <Link
                          to={item.href || '#'}
                          className={`block p-2 rounded text-base font-sans font-semibold tracking-tight ${
                            item.isDisabled
                              ? 'text-slate-400 cursor-not-allowed'
                              : isActive(item.href!)
                                ? 'text-slate-900 bg-slate-100'
                                : 'text-slate-700 hover:bg-slate-100'
                          }`}
                        >
                          {item.icon}
                          {!isCollapsed && <span className="ml-2 text-base font-sans font-semibold tracking-tight">{item.text}</span>}
                        </Link>
                      )
                    )}
                  </li>
                </PermissionFlags>
              ))}
            </ul>
            <button 
              onClick={toggleSidebar} 
              className={`absolute ${isCollapsed ? '-right-5' : '-right-9' } -top-5 bg-white p-1 z-5 rounded-full shadow-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300`}
            >
              {isCollapsed ? (
                <ChevronRight className="h-5 w-5" />
              ) : (
                <ChevronLeft className="h-5 w-5" />
              )}
            </button>
          </nav>
        </div>
      </div>
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[625px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-600">{t("website.upgrade.title")}</DialogTitle>
            <DialogDescription className="text-lg font-semibold">
              {t("website.upgrade.description")} <span className="text-green-500">$399</span>!
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <p className="font-medium">{t("website.upgrade.trigger1")}.</p>
            <p className="font-medium">{t("website.upgrade.trigger2")}</p>
            <ul className="space-y-2">
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span>{t("website.upgrade.benefit1.1")} <strong>{t("website.upgrade.benefit1.2")}</strong> {t("website.upgrade.benefit1.3")}.</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span>{t("website.upgrade.benefit2.1")} <strong>{t("website.upgrade.benefit2.2")}</strong> {t("website.upgrade.benefit2.3")}.</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span>{t("website.upgrade.benefit3.1")} <strong>{t("website.upgrade.benefit3.2")}</strong> {t("website.upgrade.benefit3.3")}</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span>{t("website.upgrade.benefit4.1")} <strong>{t("website.upgrade.benefit4.2")}</strong> {t("website.upgrade.benefit4.3")}.</span>
              </li>
            </ul>
            <p className="font-medium">{t("website.upgrade.footer")}&nbsp;<a href="mailto:hello@eateliers.com" className="text-blue-600 hover:underline">hello@eateliers.com</a></p>
          </div>
          <ButtonV2 onClick={() => setIsModalOpen(false)} className="w-full text-white font-bold py-2 px-4 rounded transition duration-300">
            {t("global.close")}
          </ButtonV2>
        </DialogContent>
      </Dialog>
    </aside>
  );
};

export default Sidebar;